/* Home A storefront interactions. No framework or design-preview runtime. */
export function normalizeQuantity(value, { min = 1, max = Number.MAX_SAFE_INTEGER, step = 1 } = {}) {
  min = Math.max(1, Number(min) || 1);
  step = Math.max(1, Number(step) || 1);
  max = Number(max) > 0 ? Math.max(min, Number(max)) : Number.MAX_SAFE_INTEGER;
  let number = Number(value);
  if (!Number.isFinite(number)) number = min;
  number = Math.min(max, Math.max(min, Math.floor(number)));
  return min + Math.floor((number - min) / step) * step;
}

async function request(url, options = {}, fetcher = globalThis.fetch) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try { return await fetcher(url, { credentials: 'same-origin', ...options, signal: controller.signal }); }
  finally { clearTimeout(timeout); }
}

export async function addCartItem(url, variantId, quantity, fetcher = globalThis.fetch, { sellingPlanId } = {}) {
  if (!/^\d+$/.test(String(variantId))) throw Error('Choose a valid product variant.');
  const item = { id: String(variantId), quantity: normalizeQuantity(quantity) };
  if (sellingPlanId !== undefined && sellingPlanId !== null && sellingPlanId !== '') {
    if (!/^\d+$/.test(String(sellingPlanId))) throw Error('Choose a valid selling plan.');
    item.selling_plan = String(sellingPlanId);
  }
  const response = await request(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [item] })
  }, fetcher);
  const result = await response.json().catch(() => null);
  if (!response.ok || !result) throw Error(typeof result?.description === 'string' ? result.description : 'Unable to add this item. Please try again.');
  return result;
}

export function createSearchController({ fetchResults, render, onError }) {
  let sequence = 0; let controller;
  return {
    cancel() { sequence++; controller?.abort(); },
    async search(query) {
      const current = ++sequence;
      controller?.abort(); controller = new AbortController();
      try {
        const results = await fetchResults(query, controller.signal);
        if (current === sequence) render(results);
      } catch (error) {
        if (current === sequence && error.name !== 'AbortError') onError(error);
      }
    }
  };
}

export function priceRangeError(minimum, maximum) {
  const min = String(minimum ?? '').trim(); const max = String(maximum ?? '').trim();
  if (min && (!Number.isFinite(Number(min)) || Number(min) < 0)) return 'Enter a valid minimum price of zero or more.';
  if (max && (!Number.isFinite(Number(max)) || Number(max) < 0)) return 'Enter a valid maximum price of zero or more.';
  if (min && max && Number(min) > Number(max)) return 'The minimum price must not exceed the maximum price.';
  return '';
}

function setupCatalog(section) {
  const form = section.querySelector('[data-catalog-form]');
  if (form) {
    const message = form.querySelector('[data-price-error]');
    const validate = () => {
      const errors = [];
      form.querySelectorAll('[data-price-range]').forEach(range => {
        const minimum = range.querySelector('[data-price-min]'); const maximum = range.querySelector('[data-price-max]');
        if (!minimum || !maximum) return;
        const error = priceRangeError(minimum.value, maximum.value);
        minimum.setCustomValidity(error);
        minimum.setAttribute('aria-invalid', String(Boolean(error)));
        if (error) errors.push(error);
      });
      if (message) { message.textContent = errors.join(' '); message.hidden = !errors.length; }
      return !errors.length;
    };
    form.querySelectorAll('[data-price-min], [data-price-max]').forEach(input => input.addEventListener('input', validate));
    form.addEventListener('invalid', event => {
      // Make invalid inputs focusable even when a native disclosure is closed.
      let parent = event.target.parentElement;
      while (parent && parent !== form) { if (parent.tagName === 'DETAILS') parent.open = true; parent = parent.parentElement; }
    }, true);
    form.addEventListener('submit', event => { if (!validate()) { event.preventDefault(); form.reportValidity(); } });
    form.querySelector('[data-catalog-sort]')?.addEventListener('change', () => { validate(); form.requestSubmit(); });
    validate();
  }
  const storageKey = 'reno-catalog-view';
  const buttons = [...section.querySelectorAll('[data-product-view]')];
  buttons.forEach(button => button.addEventListener('click', () => {
    try { localStorage.setItem(storageKey, button.dataset.productView); } catch { /* Storage may be disabled; the current view still works. */ }
  }));
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved === 'grid' || saved === 'list') buttons.find(button => button.dataset.productView === saved)?.click();
  } catch { /* Native defaults remain usable without local storage. */ }
}

const bound = new WeakSet();
let cartSequence = 0;
function config() { return JSON.parse(document.getElementById('reno-config')?.textContent || '{}'); }
function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
function localURL(url, fallback) {
  try { const parsed = new URL(url, location.origin); return parsed.origin === location.origin ? parsed.pathname + parsed.search + parsed.hash : fallback; }
  catch { return fallback; }
}
function setupSearch(form, settings) {
  const input = form.querySelector('[name="q"]');
  const box = form.querySelector('[data-search-results]');
  if (!input || !box) return;
  let timer; let query = ''; let suppressFocus = false;
  const close = () => { clearTimeout(timer); engine.cancel(); box.hidden = true; input.setAttribute('aria-expanded', 'false'); };
  const show = () => { box.hidden = false; input.setAttribute('aria-expanded', 'true'); };
  const allResults = () => {
    const link = element('a', 'reno-search__all', `View all results for “${query}” →`);
    const url = new URL(settings.routes.search, location.origin); url.searchParams.set('q', query); url.searchParams.set('type', 'product');
    link.href = url.pathname + url.search; box.append(link);
  };
  const engine = createSearchController({
    fetchResults: async (term, signal) => {
      const url = new URL(settings.routes.predictiveSearch, location.origin);
      url.searchParams.set('q', term); url.searchParams.set('resources[type]', 'product,collection');
      url.searchParams.set('resources[limit]', '4');
      const response = await fetch(url.pathname + url.search, { signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw Error('Search suggestions are unavailable.');
      return response.json();
    },
    render: data => {
      const results = data.resources?.results || {};
      box.replaceChildren();
      const products = results.products || []; const collections = results.collections || [];
      if (!products.length && !collections.length) box.append(element('p', 'reno-search__status', `No matches for “${query}”. Try a different model or product name.`));
      for (const collection of collections.slice(0, 2)) {
        const link = element('a', 'reno-search__result reno-search__collection', collection.title);
        link.href = localURL(collection.url, settings.routes.search); box.append(link);
      }
      for (const product of products.slice(0, 4)) {
        const link = element('a', 'reno-search__result'); link.href = localURL(product.url, settings.routes.search);
        const image = element('span', 'reno-search__image');
        const src = typeof product.image === 'string' ? product.image : product.featured_image?.url;
        if (src && /^(https:|\/\/)/.test(src)) { const img = element('img'); img.src = src; img.alt = ''; img.width = 48; img.height = 48; image.append(img); }
        const copy = element('span', 'reno-search__copy'); copy.append(element('span', 'reno-search__title', product.title));
        if (product.vendor) copy.append(element('small', '', product.vendor));
        const price = Number(product.price);
        link.append(image, copy);
        if (Number.isFinite(price)) link.append(element('span', 'reno-search__price', new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency: settings.currency || 'CAD' }).format(price)));
        box.append(link);
      }
      allResults(); show();
    },
    onError: () => { box.replaceChildren(element('p', 'reno-search__status', 'Suggestions are unavailable. Use Search to see all results.')); allResults(); show(); }
  });
  const search = () => {
    query = input.value.trim(); close();
    if (query.length < 2) return;
    box.replaceChildren(element('p', 'reno-search__status', 'Searching…')); show();
    timer = setTimeout(() => engine.search(query), 180);
  };
  input.addEventListener('input', search);
  input.addEventListener('focus', () => { if (!suppressFocus) search(); });
  form.addEventListener('submit', close);
  form.addEventListener('focusout', event => { if (!form.contains(event.relatedTarget)) close(); });
  form.addEventListener('keydown', event => {
    if (event.key === 'Escape') { event.preventDefault(); close(); suppressFocus = true; input.focus(); suppressFocus = false; return; }
    if (!['ArrowDown','ArrowUp'].includes(event.key) || box.hidden) return;
    const links = [...box.querySelectorAll('a')]; if (!links.length) return;
    event.preventDefault();
    const position = links.indexOf(document.activeElement);
    if (event.key === 'ArrowUp' && position <= 0) { suppressFocus = true; input.focus(); suppressFocus = false; }
    else links[(position + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length].focus();
  });
}

function setupProduct(form, settings) {
  const quantity = form.querySelector('[name="quantity"]');
  const button = form.querySelector('[data-add-button]');
  const message = form.querySelector('[data-product-message]');
  if (!quantity || !button) return;
  const limits = () => ({ min: quantity.min, max: quantity.max, step: quantity.step });
  quantity.addEventListener('change', () => { quantity.value = normalizeQuantity(quantity.value, limits()); });
  form.querySelectorAll('[data-quantity-step]').forEach(control => control.addEventListener('click', () => {
    quantity.value = normalizeQuantity(Number(quantity.value) + Number(control.dataset.quantityStep) * (Number(quantity.step) || 1), limits());
  }));
  form.addEventListener('submit', async event => {
    event.preventDefault(); if (button.disabled) return;
    const original = button.textContent;
    quantity.value = normalizeQuantity(quantity.value, limits());
    button.disabled = true; button.textContent = 'Adding…'; form.setAttribute('aria-busy', 'true');
    if (message) { message.textContent = ''; message.dataset.state = ''; }
    try {
      await addCartItem(settings.routes.cartAdd, form.elements.namedItem('id').value, Number(quantity.value));
      button.textContent = 'Added';
      if (message) {
        message.dataset.state = 'success'; message.append(document.createTextNode('Added to cart. '));
        const link = element('a', '', 'View cart'); link.href = settings.routes.cart; message.append(link);
      }
      const current = ++cartSequence;
      try {
        const response = await request(settings.routes.cartJson, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw Error('Cart refresh failed');
        const cart = await response.json();
        if (current === cartSequence) document.querySelectorAll('[data-cart-count]').forEach(count => { count.textContent = String(cart.item_count); });
      } catch { /* Item was added; retain success and cart link if the count refresh fails. */ }
      document.dispatchEvent(new CustomEvent('reno:cart-added'));
    } catch (error) {
      if (message) { message.dataset.state = 'error'; message.textContent = error.name === 'AbortError' ? 'The request timed out. Check your cart before trying again.' : (error.message || 'Unable to add this item. Please try again.'); }
    } finally {
      button.disabled = false; button.textContent = original; form.removeAttribute('aria-busy');
    }
  });
}

function setupTabs(section) {
  const tabs = [...section.querySelectorAll('[data-collection-tab]')];
  const activate = selected => {
    tabs.forEach(tab => { const active = tab === selected; tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1; });
    section.querySelectorAll('[data-collection-panel]').forEach(panel => { panel.hidden = panel.id !== selected.getAttribute('aria-controls'); });
  };
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activate(tab));
    tab.addEventListener('keydown', event => {
      if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? tabs[0] : event.key === 'End' ? tabs.at(-1) : tabs[(index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
      activate(next); next.focus();
    });
  });
  section.querySelectorAll('[data-product-view]').forEach(button => button.addEventListener('click', () => {
    section.dataset.view = button.dataset.productView;
    section.querySelectorAll('[data-product-view]').forEach(other => other.setAttribute('aria-pressed', String(other === button)));
  }));
}

function setupMenu(header) {
  const dialog = header.querySelector('[data-mobile-menu]'); if (!dialog) return;
  const opener = header.querySelector('[data-menu-open]'); let previousOverflow;
  opener?.addEventListener('click', () => {
    previousOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden';
    dialog.showModal(); opener.setAttribute('aria-expanded', 'true');
  });
  dialog.querySelectorAll('[data-menu-close]').forEach(button => button.addEventListener('click', () => dialog.close()));
  dialog.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } });
  dialog.addEventListener('close', () => { document.body.style.overflow = previousOverflow || ''; opener?.setAttribute('aria-expanded','false'); opener?.focus(); });
}

export function initializeRedesign(root = document) {
  const settings = config(); if (!settings.routes) return;
  const setup = (selector, callback) => root.querySelectorAll(selector).forEach(node => { if (!bound.has(node)) { bound.add(node); callback(node, settings); } });
  setup('[data-reno-search]', setupSearch); setup('[data-product-form]', setupProduct);
  setup('.reno-featured', section => { setupTabs(section); if (section.matches('[data-reno-catalog]')) setupCatalog(section); }); setup('[data-reno-header]', setupMenu);
}

if (typeof document !== 'undefined') {
  initializeRedesign();
  document.addEventListener('shopify:section:load', event => initializeRedesign(event.target));
}
